<!-- Loading skeleton -->
@if (isLoading) {
  <div class="flex items-center justify-center h-[calc(100vh-64px)]">
    <div class="flex items-center gap-3 text-gray-500 dark:text-gray-400">
      <svg class="animate-spin w-5 h-5 text-brand-500" fill="none" viewBox="0 0 24 24">
        <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"/>
        <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z"/>
      </svg>
      <span class="text-sm">Cargando editor...</span>
    </div>
  </div>
} @else {
  <div class="flex flex-col h-[calc(100vh-64px)]">

    <!-- ── Header ─────────────────────────────────────────────────────────── -->
    <header class="flex-shrink-0 grid grid-cols-[1fr_auto_1fr] items-center px-5 py-3 bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-800 shadow-sm">

      <!-- Left: back + name -->
      <div class="flex items-center gap-3 min-w-0">
        <button (click)="goBack()"
          class="flex-shrink-0 inline-flex items-center justify-center w-8 h-8 rounded-lg text-gray-500 hover:text-gray-900 hover:bg-gray-100 dark:text-gray-400 dark:hover:text-white dark:hover:bg-gray-800 transition-colors">
          <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7"/>
          </svg>
        </button>

        <div class="min-w-0">
          <h1 class="text-sm font-semibold text-gray-900 dark:text-white truncate max-w-xs">
            {{ state.form?.name || 'Editor de formulario' }}
          </h1>
          <div class="flex items-center gap-2 mt-0.5">
            @if (state.form?.status) {
              <span class="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium"
                [class]="formStatusBadgeClass">
                {{ state.form!.status | formStatus }}
              </span>
            }
            @if (isSaving) {
              <span class="text-[10px] text-brand-500 font-medium animate-pulse">● Guardando...</span>
            } @else if (saveError) {
              <span class="text-[10px] text-error-500 font-medium" [title]="saveError">● Error al guardar</span>
            } @else if (state.isDirty) {
              <span class="text-[10px] text-amber-500 font-medium">● Sin guardar</span>
            } @else if (lastSavedAt) {
              <span class="text-[10px] text-success-500 font-medium">● Guardado {{ lastSavedAt | date:'HH:mm' }}</span>
            } @else {
              <span class="text-[10px] text-success-500 font-medium">● Guardado</span>
            }
          </div>
        </div>
      </div>

      <!-- Center: Tabs -->
      <nav class="flex items-center gap-1">
        @for (tab of tabs; track tab.id) {
          <button
            (click)="setTab(tab.id)"
            class="px-3 py-1.5 text-xs font-medium rounded-lg transition-colors"
            [class.bg-brand-500]="activeTab === tab.id"
            [class.text-white]="activeTab === tab.id"
            [class.dark:bg-white]="activeTab === tab.id"
            [class.dark:text-gray-900]="activeTab === tab.id"
            [class.text-gray-600]="activeTab !== tab.id"
            [class.dark:text-gray-400]="activeTab !== tab.id"
            [class.hover:bg-gray-100]="activeTab !== tab.id"
            [class.dark:hover:bg-gray-800]="activeTab !== tab.id"
          >{{ tab.label }}</button>
        }
      </nav>

      <!-- Right: actions -->
      <div class="flex items-center justify-end gap-2">
        @if (!isTemplate) {
          <a [routerLink]="['/marketing-forms', formId, 'submissions']"
            class="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors">
            <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 17v-6m4 6V7m4 10v-2M5 21h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v14a2 2 0 002 2z"/></svg>
            Envíos
          </a>
        }
        @if (activeTab === 'fields') {
          <button (click)="previewOpen = true"
            class="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors">
            <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"/>
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"/>
            </svg>
            Vista previa
          </button>

          @if (isTemplate) {
            <!-- Plantilla: solo Guardar, sin dropdown ni Publicar -->
            <button (click)="save()" [disabled]="isSaving"
              class="inline-flex items-center gap-1.5 px-4 py-1.5 text-xs font-medium rounded-lg bg-brand-500 hover:bg-brand-600 text-white disabled:opacity-60 transition-colors">
              @if (isSaving) {
                <svg class="animate-spin w-3.5 h-3.5" fill="none" viewBox="0 0 24 24">
                  <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"/>
                  <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z"/>
                </svg>
                Guardando...
              } @else {
                <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"/>
                </svg>
                Guardar
              }
            </button>
          } @else {
            <!-- Formulario normal: split button + Publicar/Pausar -->
            <div class="relative flex" (click)="$event.stopPropagation()">
              <button (click)="save()" [disabled]="isSaving || isSavingTemplate"
                class="inline-flex items-center gap-1.5 pl-4 pr-3 py-1.5 text-xs font-medium rounded-l-lg bg-brand-500 hover:bg-brand-600 text-white disabled:opacity-60 transition-colors border-r border-brand-600/40">
                @if (isSaving) {
                  <svg class="animate-spin w-3.5 h-3.5" fill="none" viewBox="0 0 24 24">
                    <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"/>
                    <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z"/>
                  </svg>
                  Guardando...
                } @else {
                  <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"/>
                  </svg>
                  Guardar
                }
              </button>
              <button (click)="saveDropdownOpen = !saveDropdownOpen" [disabled]="isSaving || isSavingTemplate"
                class="inline-flex items-center px-1.5 py-1.5 text-xs font-medium rounded-r-lg bg-brand-500 hover:bg-brand-600 text-white disabled:opacity-60 transition-colors">
                <svg class="w-3.5 h-3.5 transition-transform duration-150" [class.rotate-180]="saveDropdownOpen"
                  fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"/>
                </svg>
              </button>
              @if (saveDropdownOpen) {
                <div class="absolute top-full right-0 mt-1.5 w-52 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl shadow-lg py-1 z-20">
                  <button type="button"
                    (click)="openTemplateModal(); saveDropdownOpen = false"
                    class="w-full flex items-center gap-2.5 px-3 py-2 text-xs text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors">
                    <svg class="w-3.5 h-3.5 text-gray-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z"/>
                    </svg>
                    Guardar como plantilla
                  </button>
                </div>
              }
            </div>

            @if (canPublish) {
              <button (click)="onPublish()" [disabled]="isPublishing"
                class="inline-flex items-center gap-1.5 px-4 py-1.5 text-xs font-medium rounded-lg bg-brand-500 text-white hover:bg-brand-600 disabled:opacity-60 disabled:cursor-not-allowed transition-colors">
                @if (isPublishing) {
                  <svg class="animate-spin w-3.5 h-3.5" fill="none" viewBox="0 0 24 24">
                    <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"/>
                    <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z"/>
                  </svg>
                  Publicando...
                } @else {
                  <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/>
                  </svg>
                  Publicar
                }
              </button>
            }

            @if (canPause) {
              <button (click)="onPause()" [disabled]="isPublishing"
                class="inline-flex items-center gap-1.5 px-4 py-1.5 text-xs font-medium rounded-lg bg-error-500 text-white hover:bg-error-600 disabled:opacity-60 transition-colors">
                @if (isPublishing) {
                  <svg class="animate-spin w-3.5 h-3.5" fill="none" viewBox="0 0 24 24">
                    <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"/>
                    <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z"/>
                  </svg>
                  Pausando...
                } @else {
                  <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 9v6m4-6v6m7-3a9 9 0 11-18 0 9 9 0 0118 0z"/>
                  </svg>
                  Pausar
                }
              </button>
            }
          }
        }
      </div>
    </header>

    <!-- ── Template notice ───────────────────────────────────────────────── -->
    @if (isTemplate) {
      <div class="flex-shrink-0 flex items-center gap-2.5 px-5 py-2.5 bg-indigo-50 dark:bg-indigo-500/10 border-b border-indigo-200 dark:border-indigo-500/20 text-indigo-700 dark:text-indigo-300 text-xs">
        <svg class="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
        </svg>
        <span>Estás editando una <strong>plantilla</strong>. Los formularios ya creados a partir de ella no se actualizarán con estos cambios.</span>
      </div>
    }

    <!-- ── Tab bodies ──────────────────────────────────────────────────────── -->
    @switch (activeTab) {

      @case ('fields') {
        <div class="flex flex-1 overflow-hidden">
          <aside class="w-60 flex-shrink-0 border-r border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 overflow-y-auto">
            <app-field-palette [usedTypes]="usedFieldTypes" />
          </aside>
          <main class="flex-1 overflow-hidden bg-gray-50 dark:bg-gray-950">
            <app-form-canvas
              (fieldDroppedFromPalette)="onFieldDropped($event)"
              (removeFieldAt)="onRemoveField($event)"
            />
          </main>
          <aside class="w-72 flex-shrink-0 border-l border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 overflow-y-auto">
            <app-field-properties (styleSettingsChange)="onStyleSettingsChange($event)" />
          </aside>
        </div>
      }

      @case ('settings') {
        <div class="flex-1 overflow-y-auto bg-gray-50 dark:bg-gray-950">
          @if (settingsLoading) {
            <p class="px-6 py-6 text-sm text-gray-500 dark:text-gray-400">Cargando configuración…</p>
          } @else {
            <app-form-settings-panel
              [settings]="settings"
              [saving]="settingsSaving"
              [fields]="state.fields"
              (saveChanges)="onSaveSettings($event)"
            />
          }
        </div>
      }

      @case ('urls') {
        <div class="flex-1 overflow-y-auto bg-gray-50 dark:bg-gray-950">
          <app-form-target-urls-panel
            [urls]="targetUrls"
            [loading]="urlsLoading"
            [saving]="urlsSaving"
            (createUrl)="onCreateUrl($event)"
            (updateUrl)="onUpdateUrl($event)"
            (deleteUrl)="onDeleteUrl($event)"
          />
        </div>
      }

      @case ('crm') {
        <div class="flex-1 overflow-hidden">
          <app-form-crm-mapping-panel
            [rows]="crmRows"
            [loading]="crmLoading"
            [hasUnsavedFields]="hasUnsavedFields"
            [formId]="formId"
            (mappingChanged)="onMappingChanged($event)"
            (fixedValueChanged)="onFixedValueChanged($event)"
            (catalogOptionsSync)="onCatalogOptionsSync($event)"
          />
        </div>
      }

      @case ('share') {
        <div class="flex-1 overflow-y-auto bg-gray-50 dark:bg-gray-950 p-6">
          <div class="max-w-2xl mx-auto space-y-6">

            <!-- Status notice -->
            @if (state.form?.status !== 'PUBLISHED') {
              <div class="flex items-start gap-3 p-4 bg-amber-50 dark:bg-amber-500/10 border border-amber-200 dark:border-amber-500/30 rounded-xl">
                <svg class="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"/>
                </svg>
                <div>
                  <p class="text-sm font-medium text-amber-800 dark:text-amber-400">Formulario no publicado</p>
                  <p class="text-sm text-amber-700 dark:text-amber-500 mt-0.5">El enlace no funcionará hasta que publiques el formulario. Puedes copiarlo ahora para usarlo después.</p>
                </div>
              </div>
            } @else {
              <div class="flex items-start gap-3 p-4 bg-success-50 dark:bg-success-500/10 border border-success-200 dark:border-success-500/30 rounded-xl">
                <svg class="w-5 h-5 text-success-500 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/>
                </svg>
                <div>
                  <p class="text-sm font-medium text-success-800 dark:text-success-400">Formulario activo</p>
                  <p class="text-sm text-success-700 dark:text-success-500 mt-0.5">El formulario está publicado y recibiendo respuestas.</p>
                </div>
              </div>
            }

            <!-- Public URL card -->
            <div class="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl p-6 space-y-4">
              <div>
                <h2 class="text-sm font-semibold text-gray-900 dark:text-white">Enlace público del formulario</h2>
                <p class="text-xs text-gray-500 dark:text-gray-400 mt-0.5">Comparte este enlace para que los usuarios puedan rellenar el formulario.</p>
              </div>

              <div class="flex items-center gap-2">
                <div class="flex-1 min-w-0 px-3 py-2.5 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg">
                  <p class="text-sm text-gray-700 dark:text-gray-300 truncate font-mono">{{ getPublicUrl() }}</p>
                </div>

                <button (click)="copyPublicUrl()"
                  class="flex-shrink-0 inline-flex items-center gap-1.5 px-3 py-2.5 text-xs font-medium rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors">
                  <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"/>
                  </svg>
                  Copiar
                </button>

                <a [href]="getPublicUrl()" target="_blank" rel="noopener"
                  class="flex-shrink-0 inline-flex items-center gap-1.5 px-3 py-2.5 text-xs font-medium rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors">
                  <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"/>
                  </svg>
                  Abrir
                </a>
              </div>

              <p class="text-xs text-gray-400 dark:text-gray-500">
                Slug: <span class="font-mono">{{ state.form?.slug }}</span>
              </p>
            </div>

          </div>
        </div>
      }
    }

  </div>

  <!-- Preview modal -->
  <app-form-preview
    [isOpen]="previewOpen"
    (closePreview)="previewOpen = false"
  />

  <!-- Save as template modal -->
  @if (showTemplateModal) {
    <div class="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm"
      (click)="cancelTemplateModal()">
      <div class="w-full max-w-sm mx-4 bg-white dark:bg-gray-900 rounded-2xl shadow-xl border border-gray-200 dark:border-gray-700 p-6"
        (click)="$event.stopPropagation()">

        <div class="flex items-center gap-3 mb-4">
          <div class="flex items-center justify-center w-9 h-9 rounded-xl bg-brand-50 dark:bg-brand-500/15 flex-shrink-0">
            <svg class="w-5 h-5 text-brand-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z"/>
            </svg>
          </div>
          <div>
            <h2 class="text-sm font-semibold text-gray-900 dark:text-white">Guardar como plantilla</h2>
            <p class="text-xs text-gray-500 dark:text-gray-400 mt-0.5">Se creará una copia reutilizable de este formulario.</p>
          </div>
        </div>

        <label class="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1.5">
          Nombre de la plantilla
        </label>
        <input
          type="text"
          [(ngModel)]="templateName"
          (keydown.enter)="confirmSaveAsTemplate()"
          (keydown.escape)="cancelTemplateModal()"
          placeholder="Ej. Formulario de contacto base"
          class="w-full px-3 py-2 text-sm rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-brand-500 mb-4"
          autofocus />

        <div class="flex gap-2 justify-end">
          <button type="button" (click)="cancelTemplateModal()"
            class="px-4 py-2 text-xs font-medium rounded-lg border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors">
            Cancelar
          </button>
          <button type="button" (click)="confirmSaveAsTemplate()"
            [disabled]="!templateName.trim() || isSavingTemplate"
            class="inline-flex items-center gap-1.5 px-4 py-2 text-xs font-medium rounded-lg bg-brand-500 hover:bg-brand-600 text-white disabled:opacity-60 disabled:cursor-not-allowed transition-colors">
            @if (isSavingTemplate) {
              <svg class="animate-spin w-3.5 h-3.5" fill="none" viewBox="0 0 24 24">
                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"/>
                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z"/>
              </svg>
              Guardando...
            } @else {
              Guardar plantilla
            }
          </button>
        </div>

      </div>
    </div>
  }
}
